import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Download,
  Printer,
  Share2,
  Check,
  Calendar,
  User,
  Mail,
  MapPin,
  Phone,
  ArrowLeft
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const Invoice = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  // Sample invoice data
  const invoice = {
    id: orderId || 'ORD-001',
    date: new Date(),
    status: 'paid',
    customer: {
      name: 'John Doe',
      email: 'john@example.com',
      phone: '+1 (555) 000-0000',
      address: '123 Main Street, New York, NY 10001'
    },
    items: [
      { name: 'Premium Website Template', quantity: 1, price: 49.99 },
      { name: 'E-commerce Plugin', quantity: 1, price: 29.99 }
    ],
    subtotal: 79.98,
    tax: 8.00,
    shipping: 9.99,
    total: 97.97,
    paymentMethod: 'Credit Card (**** 4242)'
  };

  const handleDownload = () => {
    toast.success('Invoice downloaded!');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Actions */}
        <div className="flex items-center justify-between mb-6 no-print">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-white"
            >
              <Download className="w-5 h-5" />
              Download
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-white"
            >
              <Printer className="w-5 h-5" />
              Print
            </button>
            <button
              onClick={() => toast.success('Invoice shared!')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
            >
              <Share2 className="w-5 h-5" />
              Share
            </button>
          </div>
        </div>

        {/* Invoice */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-purple-600 p-8 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Check className="w-6 h-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">INVOICE</h1>
                    <p className="text-primary-100">Hostbit Cloud</p>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-400 text-green-900 text-sm font-medium rounded-full">
                  <Check className="w-4 h-4" />
                  Paid
                </span>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-8">
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Invoice To</h3>
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">{invoice.customer.name}</p>
                  <p className="text-gray-600 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {invoice.customer.email}
                  </p>
                  <p className="text-gray-600 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {invoice.customer.phone}
                  </p>
                  <p className="text-gray-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {invoice.customer.address}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Invoice Details</h3>
                <div className="space-y-1">
                  <p className="text-gray-600">
                    <span className="font-medium">Invoice #:</span> {invoice.id}
                  </p>
                  <p className="text-gray-600 flex items-center justify-end gap-2">
                    <Calendar className="w-4 h-4" />
                    {format(invoice.date, 'MMMM dd, yyyy')}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Payment:</span> {invoice.paymentMethod}
                  </p>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <table className="w-full mb-8">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Item</th>
                  <th className="text-center py-3 text-sm font-medium text-gray-500">Quantity</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Price</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-4 text-gray-900">{item.name}</td>
                    <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                    <td className="py-4 text-right text-gray-600">${item.price.toFixed(2)}</td>
                    <td className="py-4 text-right font-medium">${(item.price * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${invoice.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>${invoice.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>${invoice.shipping.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between text-lg font-semibold text-gray-900">
                  <span>Total</span>
                  <span>${invoice.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-500">Thank you for your business!</p>
              <p className="text-sm text-gray-400 mt-1">
                If you have any questions, please contact support@hostbit.cloud
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
